import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { Paper, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
    chartCard: {
        background: theme.palette.type === 'dark'
            ? 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)',
        padding: theme.spacing(3),
        borderRadius: 16,
        border: `1px solid ${theme.palette.type === 'dark' ? '#334155' : '#e0e0e0'}`,
        boxShadow: theme.palette.type === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.6s ease',
    },
    chartTitle: {
        marginBottom: theme.spacing(3),
        fontSize: '2rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    noDataText: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
        fontSize: '1rem',
    },
}));

function StatusDistributionChart({ loading, data }) {
    const classes = useStyles();
    const theme = useTheme();
    const chartRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [animationCount, setAnimationCount] = useState(0);
    const [chartKey, setChartKey] = useState(0);

    // Intersection Observer to detect when chart is in viewport
    useEffect(() => {
        // Only set up observer if chart hasn't animated 2 times yet
        if (animationCount >= 2) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !loading && data && animationCount < 2) {
                    setIsVisible(true);
                    setAnimationCount(prev => prev + 1);
                    // Force chart re-render to trigger animation
                    setChartKey(prev => prev + 1);
                } else if (!entry.isIntersecting) {
                    setIsVisible(false);
                }
            },
            {
                threshold: 0.3,
                rootMargin: '0px'
            }
        );

        if (chartRef.current) {
            observer.observe(chartRef.current);
        }

        return () => {
            if (chartRef.current) {
                observer.unobserve(chartRef.current);
            }
        };
    }, [loading, data, animationCount]);

    // Memoize chart data
    const chartData = useMemo(() => {
        if (!data || !data.status_distribution) return null;

        const statusDistribution = data.status_distribution || [];
        const labels = statusDistribution.map(item =>
            item.status.charAt(0).toUpperCase() + item.status.slice(1)
        );
        const percentages = statusDistribution.map(item => parseFloat(item.percentage));

        // Status-based colors
        const getStatusColor = (status) => {
            switch (status.toLowerCase()) {
                case 'read':
                    return '#3b82f6';
                case 'delivered':
                    return '#10b981';
                case 'sent':
                    return '#f59e0b';
                case 'failed':
                    return '#ef4444';
                default:
                    return '#94a3b8';
            }
        };

        const backgroundColors = statusDistribution.map(item => getStatusColor(item.status));

        return {
            labels: labels,
            datasets: [{
                label: 'Percentage',
                data: percentages,
                backgroundColor: backgroundColors,
                borderRadius: 6,
                barThickness: 40,
            }]
        };
    }, [data]);

    // Memoize chart options
    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: false
        },
        tooltips: {
            backgroundColor: theme.palette.type === 'dark'
                ? 'rgba(30, 41, 59, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            titleFontColor: theme.palette.text.primary,
            bodyFontColor: theme.palette.text.secondary,
            borderColor: theme.palette.type === 'dark' ? '#334155' : '#e5e7eb',
            borderWidth: 1,
            cornerRadius: 12,
            displayColors: true,
            bodySpacing: 6,
            titleSpacing: 8,
            footerSpacing: 6,
            xPadding: 12,
            yPadding: 12,
            callbacks: {
                label: (tooltipItem, chartData) => {
                    const dataIndex = tooltipItem.index;
                    const percentage = tooltipItem.value;
                    const count = data.status_distribution[dataIndex]?.count || 0;
                    return [
                        `Percentage: ${percentage}%`,
                        `Count: ${count.toLocaleString()}`
                    ];
                }
            }
        },
        scales: {
            xAxes: [{
                gridLines: {
                    display: false
                },
                ticks: {
                    fontColor: theme.palette.text.primary,
                    fontSize: 12,
                    fontStyle: '500',
                    padding: 10
                }
            }],
            yAxes: [{
                gridLines: {
                    color: theme.palette.divider || '#EEE',
                    borderDash: [3, 3],
                    drawBorder: false
                },
                ticks: {
                    fontColor: theme.palette.text.primary,
                    fontSize: 12,
                    fontStyle: '500',
                    padding: 10,
                    beginAtZero: true,
                    callback: function (value) {
                        return value + '%';
                    }
                }
            }]
        },
        animation: {
            duration: animationCount <= 2 && isVisible ? 2000 : 0,
            easing: 'easeInOutCubic',
        }
    }), [theme.palette, data, isVisible, animationCount]);

    // Show skeleton while loading
    if (loading) {
        return (
            <Paper className={classes.chartCard}>
                <Skeleton
                    variant="text"
                    width="50%"
                    height={35}
                    style={{ marginBottom: '24px' }}
                    animation="wave"
                />
                <div style={{ height: '350px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: '8px' }}>
                    {[80, 60, 40, 30].map((height, index) => (
                        <Skeleton
                            key={index}
                            variant="rect"
                            height={`${height}%`}
                            style={{
                                borderRadius: 6,
                                width: '100%'
                            }}
                            animation="wave"
                        />
                    ))}
                </div>
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-around' }}>
                    {[1, 2, 3, 4].map(item => (
                        <Skeleton
                            key={item}
                            variant="text"
                            width={60}
                            height={20}
                            animation="wave"
                        />
                    ))}
                </div>
            </Paper>
        );
    }

    // Show no data message
    if (!data || !data.status_distribution || data.status_distribution.length === 0) {
        return (
            <Paper className={classes.chartCard}>
                <Typography className={classes.noDataText}>
                    No status distribution data available
                </Typography>
            </Paper>
        );
    }

    // Show chart with animation when data is loaded
    return (
        <Paper
            ref={chartRef}
            className={classes.chartCard}
            style={{
                opacity: animationCount >= 2 ? 1 : (isVisible ? 1 : 0.3),
                transform: animationCount >= 2 ? 'scale(1)' : (isVisible ? 'scale(1)' : 'scale(0.95)'),
            }}
        >
            <Typography className={classes.chartTitle}>
                Status Distribution
            </Typography>
            <div style={{ height: '350px' }}>
                {chartData && (
                    <Bar
                        key={chartKey}
                        data={chartData}
                        options={chartOptions}
                    />
                )}
            </div>
        </Paper>
    );
}

export default StatusDistributionChart;